import { TYPES } from './constants';

export function renderChart(renderContext, node) {
  if (node.type !== TYPES.chart) return;
  const { context } = renderContext;

  const { x, y, w, h } = node;

  const config = normalizeChartConfig(node);

  context.save();
  context.translate(x, y);

  if (node.rotation) {
    context.rotate((node.rotation * Math.PI) / 180);
  }

  const padding = resolvePadding(node.padding ?? config.options.padding, {
    top: 24,
    right: 24,
    bottom: 32,
    left: 40,
  });
  const bounds = {
    x: padding.left,
    y: padding.top,
    w: Math.max(0, w - padding.left - padding.right),
    h: Math.max(0, h - padding.top - padding.bottom),
  };

  if (config.options.background || node.background) {
    context.fillStyle = config.options.background || node.background;
    context.fillRect(0, 0, w, h);
  }

  switch (config.type) {
    case 'line':
      drawLineChart(context, bounds, config);
      break;
    case 'pie':
      drawPieChart(context, bounds, config);
      break;
    case 'bar':
    default:
      drawBarChart(context, bounds, config);
      break;
  }

  context.restore();
}

function normalizeChartConfig(node) {
  // Data only
  const chart = node.chart || node.data || {};
  const type = (chart.type || node.chartType || 'bar').toLowerCase();
  const labels = Array.isArray(chart.labels)
    ? chart.labels
    : Array.isArray(chart.categories)
      ? chart.categories
      : [];
  const rawSeries = Array.isArray(chart.series) ? chart.series : [];
  const series = rawSeries.map((s, i) => ({
    name: s?.name ?? `Series ${i + 1}`,
    values: Array.isArray(s?.values) ? s.values : Array.isArray(s) ? s : [],
    // keep optional s.color for backward compatibility, but palette is preferred
    color: s?.color,
  }));

  // Style only
  const style = node.style || node.chartStyle || node.theme || {};
  const styleAxes = style.axes || {};
  const styleLegend = style.legend || {};

  const options = {
    axes: {
      x: {
        show: true,
        grid: false,
        label: '',
        tickCount: 5,
        ...chart?.axes?.x,
        ...chart?.options?.axes?.x,
        ...styleAxes.x,
      },
      y: {
        show: true,
        grid: true,
        label: '',
        tickCount: 5,
        ...chart?.axes?.y,
        ...chart?.options?.axes?.y,
        ...styleAxes.y,
      },
    },
    legend: {
      show: false,
      position: 'right',
      ...chart?.legend,
      ...chart?.options?.legend,
      ...styleLegend,
    },
    background:
      style.background ?? chart?.background ?? chart?.options?.background ?? node.background,
    padding: style.padding,
    palette: Array.isArray(style.palette) && style.palette.length ? style.palette : defaultPalette,
    barGap: typeof style.barGap === 'number' ? style.barGap : 0.2,
    lineWidth: typeof style.lineWidth === 'number' ? style.lineWidth : 2,
    pointRadius: typeof style.pointRadius === 'number' ? style.pointRadius : 3,
    gridColor: style.gridColor || 'rgba(0,0,0,0.08)',
    axisColor: style.axisColor || 'rgba(0,0,0,0.15)',
  };

  return { type, labels, series, options };
}

const defaultPalette = [
  '#5B8FF9',
  '#61DDAA',
  '#65789B',
  '#F6BD16',
  '#7262FD',
  '#78D3F8',
  '#9661BC',
  '#F6903D',
  '#008685',
  '#F08BB4',
];

function resolvePadding(padding, fallback) {
  if (!padding) return fallback;
  if (typeof padding === 'number') {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  const {
    top = fallback.top,
    right = fallback.right,
    bottom = fallback.bottom,
    left = fallback.left,
  } = padding;
  return { top, right, bottom, left };
}

function computeYDomain(series) {
  let min = 0;
  let max = 0;
  for (const s of series) {
    for (const v of s.values || []) {
      if (typeof v !== 'number') continue;
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
  }
  if (min === max) {
    max = min + 1;
  }
  return { min, max };
}

function niceTicks(min, max, tickCount = 5) {
  const span = max - min;
  if (!isFinite(span) || span <= 0) return { ticks: [min, max], min, max };
  const step0 = Math.pow(10, Math.floor(Math.log10(span / Math.max(1, tickCount))));
  const err = span / (tickCount * step0);
  const step = err >= 7.5 ? step0 * 10 : err >= 3.5 ? step0 * 5 : err >= 1.5 ? step0 * 2 : step0;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step) ticks.push(v);
  return { ticks, min: niceMin, max: niceMax };
}

function drawAxesAndGrid(ctx, bounds, cfg, yScale) {
  const { x, y, w, h } = bounds;
  const axes = cfg.options.axes || {};
  ctx.save();
  ctx.strokeStyle = cfg.options.axisColor || 'rgba(0,0,0,0.15)';
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = 1;

  if (axes.y?.show !== false) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + h);
    ctx.stroke();
  }
  if (axes.x?.show !== false) {
    ctx.beginPath();
    ctx.moveTo(x, y + h);
    ctx.lineTo(x + w, y + h);
    ctx.stroke();
  }

  if (axes.y?.grid) {
    ctx.strokeStyle = cfg.options.gridColor || 'rgba(0,0,0,0.08)';
    for (const t of yScale.ticks) {
      const py = y + h - ((t - yScale.min) / (yScale.max - yScale.min)) * h;
      ctx.beginPath();
      ctx.moveTo(x, py);
      ctx.lineTo(x + w, py);
      ctx.stroke();
    }
  }

  ctx.restore();
}

function drawBarChart(ctx, bounds, cfg) {
  const { x, y, w, h } = bounds;
  const labels = cfg.labels || [];
  const series = cfg.series || [];
  const { min, max } = computeYDomain(series);
  const yScale = niceTicks(min, max, cfg.options.axes?.y?.tickCount || 5);
  drawAxesAndGrid(ctx, bounds, cfg, yScale);

  const groups = Math.max(1, labels.length);
  const seriesCount = Math.max(1, series.length);
  const groupGap = cfg.options.barGap ?? 0.2;
  const innerW = w / groups;
  const barW = (innerW * (1 - groupGap)) / seriesCount;

  for (let gi = 0; gi < groups; gi++) {
    const groupX = x + gi * innerW + (innerW * groupGap) / 2;
    for (let si = 0; si < seriesCount; si++) {
      const s = series[si];
      const v = Number(s.values[gi] ?? 0);
      const color =
        (cfg.options.palette && cfg.options.palette[si]) ||
        s.color ||
        defaultPalette[si % defaultPalette.length];
      const cy = y + h;
      const vh = ((v - yScale.min) / (yScale.max - yScale.min)) * h;
      const bh = Math.max(0, Math.min(h, vh));
      const bx = groupX + si * barW;
      const by = cy - bh;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.rect(bx, by, Math.max(1, barW - 1), bh);
      ctx.fill();
    }
  }
}

function drawLineChart(ctx, bounds, cfg) {
  const { x, y, w, h } = bounds;
  const labels = cfg.labels || [];
  const series = cfg.series || [];
  const { min, max } = computeYDomain(series);
  const yScale = niceTicks(min, max, cfg.options.axes?.y?.tickCount || 5);
  drawAxesAndGrid(ctx, bounds, cfg, yScale);

  const n = Math.max(1, labels.length - 1);
  const stepX = n === 0 ? 0 : w / n;

  for (let si = 0; si < series.length; si++) {
    const s = series[si];
    const color =
      (cfg.options.palette && cfg.options.palette[si]) ||
      s.color ||
      defaultPalette[si % defaultPalette.length];
    ctx.strokeStyle = color;
    ctx.lineWidth = cfg.options.lineWidth ?? 2;
    ctx.beginPath();
    for (let i = 0; i < labels.length; i++) {
      const v = Number(s.values[i] ?? 0);
      const px = x + stepX * i;
      const py = y + h - ((v - yScale.min) / (yScale.max - yScale.min)) * h;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();

    for (let i = 0; i < labels.length; i++) {
      const v = Number(s.values[i] ?? 0);
      const px = x + stepX * i;
      const py = y + h - ((v - yScale.min) / (yScale.max - yScale.min)) * h;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, cfg.options.pointRadius ?? 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawPieChart(ctx, bounds, cfg) {
  const { x, y, w, h } = bounds;
  const series = cfg.series || [];
  const s = series[0] || { values: [] };
  const values = s.values || [];
  const total = values.reduce((a, b) => a + (Number(b) || 0), 0) || 1;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const radius = Math.max(10, Math.min(w, h) / 2);
  let start = -Math.PI / 2;

  for (let i = 0; i < values.length; i++) {
    const v = Number(values[i] || 0);
    const angle = (v / total) * Math.PI * 2;
    const end = start + angle;
    const color =
      (cfg.options.palette && cfg.options.palette[i]) ||
      series[i]?.color ||
      s.color ||
      defaultPalette[i % defaultPalette.length];
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, radius, start, end);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    start = end;
  }
}
